import type { PermissionMatrixModel } from './permissionMatrix';

/**
 * The recovery-plane key prefixes (OPSFL-FSD-IAM-UC1 §9.G5 + UC2 §11.G10) — mirrors
 * `platform-security/.../rbac/RecoveryPlane.PREFIXES` (Java constants are not importable here;
 * the G10 build gate guards the server copy, and the server re-refuses regardless — this list
 * only decides what the PICKER OFFERS, never what is allowed).
 */
export const RECOVERY_PLANE_PREFIXES: readonly string[] = [
  'uam.', 'acl.', 'roles.', 'projects.', 'delegation.', 'abac.', 'federation.',
  'spokes.', 'profile.', 'quotas.', 'platform.',
];

/** True when `key` lives on the recovery plane (prefix match — never bindable, G10). */
export function isRecoveryPlaneKey(key: string): boolean {
  return RECOVERY_PLANE_PREFIXES.some((p) => key.startsWith(p));
}

/**
 * The keys a custom-role author may bind, per the §5.4 server guards: a matrix key (the closed
 * authority — ROLE_KEY_UNKNOWN otherwise), off the recovery plane (ROLE_KEY_NOT_BINDABLE), and
 * inside the AUTHOR's own effective key set (ROLE_KEY_EXCEEDS_AUTHOR — a role can never carry
 * authority its author lacks). This is the allowlist the picker OFFERS; the server re-checks every
 * submitted key (§12 tamper contract), and any custom-role extra keys the author holds beyond
 * their catalogue roles are simply not offered here (conservative — never wrong, only narrower).
 */
export function bindableKeys(matrix: PermissionMatrixModel, authorRoles: string[]): string[] {
  const held = new Set(authorRoles);
  return matrix.keys
    .filter((k) => !isRecoveryPlaneKey(k.key) && k.roles.some((r) => held.has(r)))
    .map((k) => k.key);
}

/**
 * The closed §5.5 scope-clause resource types — mirrors `AclKeyMap.RESOURCE_TYPES` (v1, FSD §4.2).
 * A scope clause compiles to exactly ONE UC1 CONFINE acl row; the server refuses anything outside
 * this set with a 400 naming it.
 */
export const SCOPE_RESOURCE_TYPES: readonly string[] = [
  'work_item', 'flow', 'entity', 'dataroom_invite',
];
